/**
 * ProfitabilityDashboardPage Component
 *
 * Dashboard page displaying profitability analysis by strain and customer segment.
 * Shows contribution margins, margin percentages, and drill-down tables.
 *
 * Part of US-P7-002: Profitability Analysis
 */

import { useState, useMemo } from 'react'
import { PageLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useProfitability } from '@/hooks/useProfitability'
import { useCostTrend } from '@/hooks/useCostSummary'
import PeriodSelector, { PERIOD_TYPES, calculatePeriod } from '@/components/costs/PeriodSelector'
import ProfitabilityCard from '@/components/profitability/ProfitabilityCard'
import StrainProfitabilityTable from '@/components/profitability/StrainProfitabilityTable'
import SegmentProfitabilityTable from '@/components/profitability/SegmentProfitabilityTable'
import MonthlyPnLTable from '@/components/profitability/MonthlyPnLTable'

/**
 * Map API strain data to table component format
 *
 * @param {Array} strains - Strain data from API
 * @returns {Array} Formatted data for StrainProfitabilityTable
 */
function mapStrainData(strains) {
  if (!strains || strains.length === 0) return []

  return strains.map((strain, index) => ({
    strain_id: strain.strain_name || `strain-${index}`, // Use name as ID since we don't have UUID
    strain_name: strain.strain_name || 'Unknown',
    revenue: strain.revenue || 0,
    kg_sold: strain.kg_sold || 0,
    avg_price_per_kg: strain.avg_price_per_kg || 0,
    variable_cost_per_kg: strain.variable_cost_per_kg || 0,
    contribution_margin: strain.contribution_margin || 0,
    margin_percent: strain.margin_percent || 0
  }))
}

/**
 * Map API segment data to table component format
 *
 * @param {Array} segments - Segment data from API
 * @returns {Array} Formatted data for SegmentProfitabilityTable
 */
function mapSegmentData(segments) {
  if (!segments || segments.length === 0) return []

  return segments.map((segment, index) => ({
    segment_id: segment.segment_id || `segment-${index}`,
    segment_name: segment.segment_name || 'Unknown',
    revenue: segment.revenue || 0,
    kg_sold: segment.kg_sold || 0,
    // Map API field names to component expected names
    variable_cost: segment.total_variable_cost || 0,
    contribution: segment.contribution_margin || 0,
    margin_percent: segment.margin_percent || 0,
    customer_count: segment.customer_count || 0
  }))
}

/**
 * ProfitabilityDashboardPage Component
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 */
export default function ProfitabilityDashboardPage({ user, onLogout }) {
  // Period state
  const [periodType, setPeriodType] = useState(PERIOD_TYPES.MONTH)
  const [periodData, setPeriodData] = useState(() => calculatePeriod(PERIOD_TYPES.MONTH))

  // Active tab state
  const [activeTab, setActiveTab] = useState('strain')

  // Trend months for P&L table: Month/Quarter show 12 for context, YTD/Trailing scope to period
  const trendMonths = useMemo(() => {
    if (periodType === PERIOD_TYPES.MONTH || periodType === PERIOD_TYPES.QUARTER) return 12
    const start = new Date(periodData.start)
    const end = new Date(periodData.end)
    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
  }, [periodType, periodData.start, periodData.end])

  const { trend: pnlTrend, isLoading: pnlLoading } = useCostTrend(trendMonths)

  // Fetch profitability data for selected period
  const {
    strains,
    segments,
    summary,
    variableCostPerKg,
    isLoading,
    error,
    refetch
  } = useProfitability({
    periodStart: periodData.start,
    periodEnd: periodData.end
  })

  // Map data for table components
  const strainTableData = useMemo(() => mapStrainData(strains), [strains])
  const segmentTableData = useMemo(() => mapSegmentData(segments), [segments])

  /**
   * Handle period change
   */
  const handlePeriodChange = (newPeriodType, newPeriodData) => {
    setPeriodType(newPeriodType)
    setPeriodData(newPeriodData)
  }

  /**
   * Handle strain row click (for future drill-down)
   */
  const handleStrainClick = (strainId) => {
    // Future: Navigate to strain detail view
    console.log('[Profitability] Strain clicked:', strainId)
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Profitability Analysis</h1>
            <p className="text-muted-foreground mt-1">
              {periodData.label}
            </p>
          </div>

          {/* Period Selector */}
          <PeriodSelector
            value={periodType}
            onChange={handlePeriodChange}
          />
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">
                Failed to load profitability data: {error}
              </p>
              <button
                onClick={refetch}
                className="mt-2 text-sm text-red-600 underline hover:text-red-700"
              >
                Try again
              </button>
            </CardContent>
          </Card>
        )}

        {/* Summary cards and variable cost info removed — data is in the tables below */}

        {/* Main Content - Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="strain">By Strain</TabsTrigger>
                <TabsTrigger value="segment">By Segment</TabsTrigger>
              </TabsList>

              <TabsContent value="strain">
                <StrainProfitabilityTable
                  data={strainTableData}
                  loading={isLoading}
                  onRowClick={handleStrainClick}
                />
              </TabsContent>

              <TabsContent value="segment">
                <SegmentProfitabilityTable
                  data={segmentTableData}
                  loading={isLoading}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Monthly P&L */}
        <MonthlyPnLTable trendData={pnlTrend} isLoading={pnlLoading} />

        {/* Empty State */}
        {!isLoading && !error && strains.length === 0 && segments.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <p className="text-neutral-500 mb-2">
                  No profitability data available for this period
                </p>
                <p className="text-sm text-neutral-400">
                  Sales data and production costs are required to calculate profitability
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  )
}
